import { startTransition, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Group,
  Modal,
  MultiSelect,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
  UnstyledButton,
} from '@mantine/core';
import { isNotEmpty, useForm } from '@mantine/form';
import { IconArrowNarrowLeft } from '@tabler/icons-react';
import { SurveyCreator, SurveyCreatorComponent } from 'survey-creator-react';
import { type ICreatorOptions, registerSurveyTheme } from 'survey-creator-core';
import 'survey-core/survey-core.css';
import 'survey-creator-core/survey-creator-core.css';
import { SaveErrorBox } from '@/components/SaveErrorBox/SaveErrorBox';
import { formsTemplatesService } from '@/services/formsTemplates.service';
import type { FormProgram, FormTemplateDTO, FormTemplateTagDTO } from '@/types/formsTemplates';
import type { AxiosError } from 'axios';
import type { ErrorResponse } from '@/types/error';
import SurveyTheme from 'survey-core/themes';
import styles from './FormBuilderModal.module.sass';

const PROGRAM_OPTIONS = [
  { value: 'mpa', label: 'MPA' },
  { value: 'messages', label: 'Messages' },
  { value: 'forms', label: 'Forms' },
  { value: 'camino', label: 'Camino' },
];

registerSurveyTheme(SurveyTheme);

const CONTENT_REQUIRED_MESSAGE = 'Add at least one form field in the content builder.';

function hasValidFormTemplateContent(json: Record<string, unknown>): boolean {
  const pages = json.pages;
  if (!Array.isArray(pages) || pages.length === 0) {
    return false;
  }
  return pages.some((page) => {
    if (!page || typeof page !== 'object') {
      return false;
    }
    const elements = (page as { elements?: unknown }).elements;
    return Array.isArray(elements) && elements.length > 0;
  });
}

const defaultCreatorOptions: ICreatorOptions = {
  showLogicTab: true,
  showTranslationTab: false,
  showThemeTab: true,
  autoSaveEnabled: true,
  collapseOnDrag: true,
  questionTypes: [
    'radiogroup',
    'rating',
    'checkbox',
    'dropdown',
    'tagbox',
    'file',
    'imagepicker',
    'ranking',
    'text',
    'comment',
    'panel',
    'paneldynamic',
    'matrix',
    'image',
    'signaturepad',
  ],
};

type FormBuilderFormData = {
  title: string;
  description: string;
  program: FormProgram;
  tagIds: string[];
};

type FormBuilderModalProps = {
  opened: boolean;
  onClose: () => void;
  onCreated: () => void;
  onUpdated?: () => void;
  availableTags: FormTemplateTagDTO[];
  editTemplate?: FormTemplateDTO | null;
};

export const FormBuilderModal = ({
  opened,
  onClose,
  onCreated,
  onUpdated,
  availableTags,
  editTemplate,
}: FormBuilderModalProps) => {
  const isEditMode = !!editTemplate;
  const [currentView, setCurrentView] = useState<'details' | 'builder'>('details');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<AxiosError<ErrorResponse> | null>(null);
  const [contentError, setContentError] = useState<string | null>(null);
  const [surveyJSON, setSurveyJSON] = useState<Record<string, unknown>>({});
  const creatorInstanceRef = useMemo(() => ({ current: null as SurveyCreator | null }), []);

  const initialValues: FormBuilderFormData = editTemplate
    ? {
        title: editTemplate.content?.title ?? '',
        description: editTemplate.content?.description ?? '',
        program: editTemplate.program,
        tagIds: editTemplate.tags.filter((t) => !t.isRemove).map((t) => t.id),
      }
    : { title: '', description: '', program: 'forms', tagIds: [] };

  const form = useForm<FormBuilderFormData>({
    initialValues,
    validate: {
      title: isNotEmpty('Title is required'),
      description: isNotEmpty('Description is required'),
      program: isNotEmpty('Program is required'),
    },
  });

  const tagSelectData = availableTags.map((tag) => ({ value: tag.id, label: tag.name }));

  const creator = useMemo(() => {
    const instance = new SurveyCreator(defaultCreatorOptions);
    creatorInstanceRef.current = instance;
    return instance;
  }, []);

  useEffect(() => {
    if (!opened) return;

    if (editTemplate) {
      form.setValues({
        title: editTemplate.content?.title ?? '',
        description: editTemplate.content?.description ?? '',
        program: editTemplate.program,
        tagIds: editTemplate.tags.filter((t) => !t.isRemove).map((t) => t.id),
      });
      setSurveyJSON(editTemplate.content ?? {});
      if (creator) {
        creator.JSON = editTemplate.content ?? {};
      }
    } else {
      form.setValues({ title: '', description: '', program: 'forms', tagIds: [] });
      setSurveyJSON({});
      if (creator) {
        creator.JSON = {};
      }
    }
    setCurrentView('details');
    setSaveError(null);
    setContentError(null);
  }, [opened, editTemplate?.id]);

  useEffect(() => {
    const saveContent = async (saveNo: number, callback: (saveNo: number, success: boolean) => void) => {
      const content = creator.JSON;
      startTransition(() => {
        setSurveyJSON(content);
      });

      if (editTemplate) {
        try {
          await formsTemplatesService.updateFormTemplate(editTemplate.id, {
            status: editTemplate.status,
            tags: editTemplate.tags,
            program: editTemplate.program,
            content,
          });
          callback(saveNo, true);
        } catch (_) {
          callback(saveNo, false);
        }
      } else {
        callback(saveNo, true);
      }
    };

    creator.saveSurveyFunc = saveContent;
    creator.saveThemeFunc = saveContent;
  }, [creator, editTemplate]);

  const handleClose = useCallback(() => {
    form.reset();
    setCurrentView('details');
    setSaveError(null);
    setContentError(null);
    setIsSaving(false);
    onClose();
  }, [onClose]);

  const handleGoToBuilder = () => {
    if (form.validate().hasErrors) return;
    if (creator) {
      creator.JSON = {
        ...surveyJSON,
        title: form.values.title,
        description: form.values.description,
      };
    }
    setCurrentView('builder');
  };

  const handleReturnFromBuilder = () => {
    if (creatorInstanceRef.current) {
      const json = creatorInstanceRef.current.JSON;
      setSurveyJSON(json);
      if (hasValidFormTemplateContent(json)) {
        setContentError(null);
      }
    }
    setCurrentView('details');
  };

  const handleSubmit = async (values: FormBuilderFormData) => {
    if (!hasValidFormTemplateContent(creator.JSON)) {
      setContentError(CONTENT_REQUIRED_MESSAGE);
      return;
    }
    setContentError(null);
    setIsSaving(true);
    setSaveError(null);

    const content = creator.JSON;

    try {
      const selectedTags = values.tagIds.map((id) => {
        const tag = availableTags.find((t) => t.id === id);
        return { id, name: tag?.name ?? '', isRemove: false };
      });
      const removedTags = editTemplate?.tags
        .filter((t) => !values.tagIds.includes(t.id))
        .map((t) => ({ id: t.id, name: t.name, isRemove: true }));
      if (editTemplate) {
        await formsTemplatesService.updateFormTemplate(editTemplate.id, {
          status: editTemplate.status,
          tags: [...selectedTags, ...(removedTags ?? [])],
          program: values.program,
          content,
        });
        onUpdated?.();
      } else {
        await formsTemplatesService.saveFormTemplateDraft({
          program: values.program,
          content,
          tags: [...selectedTags, ...(removedTags ?? [])],
        });
        onCreated();
      }
      handleClose();
    } catch (error) {
      setSaveError(error as AxiosError<ErrorResponse>);
    } finally {
      setIsSaving(false);
    }
  };

  if (currentView === 'builder') {
    return (
      <div className={styles.builderOverlay}>
        <Group justify="space-between" align="center" className={styles.header} p="md">
          <UnstyledButton onClick={handleReturnFromBuilder} className={styles.backButton}>
            <IconArrowNarrowLeft size={20} />
            <Text size="lg" fw={500}>
              Form Builder
            </Text>
          </UnstyledButton>
          <Group gap="xs">
            <Button variant="default" onClick={handleReturnFromBuilder}>
              Back to Details
            </Button>
            <Button variant="filled" onClick={() => form.onSubmit(handleSubmit)()} loading={isSaving}>
              {isEditMode ? 'Save Changes' : 'Save as Draft'}
            </Button>
          </Group>
        </Group>
        {(saveError || contentError) && (
          <Box px="md">
            {contentError && (
              <Text c="red" size="sm" mb={saveError ? 'xs' : 0}>
                {contentError}
              </Text>
            )}
            {saveError && <SaveErrorBox error={saveError} />}
          </Box>
        )}
        <Box className={styles.creatorContainer}>
          <SurveyCreatorComponent creator={creator} />
        </Box>
      </div>
    );
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={isEditMode ? 'Edit Form Template' : 'Create Form Template'}
      size="lg"
      classNames={{
        header: styles.modalHeader,
        title: styles.modalTitle,
        close: styles.closeButton,
      }}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md" mt="md">
          <Stack gap="md">
            <Stack gap="md">
              <TextInput
                {...form.getInputProps('title')}
                label="Title"
                placeholder="Enter form template title"
                required
              />
              <Textarea
                {...form.getInputProps('description')}
                label="Description"
                placeholder="Enter form template description"
                required
                minRows={3}
                autosize
              />
              <Group grow>
                <Select
                  {...form.getInputProps('program')}
                  label="Program"
                  data={PROGRAM_OPTIONS}
                  required
                  allowDeselect={false}
                />
              </Group>
              <Group grow>
                <Stack gap={4}>
                  <MultiSelect
                    label="Tags"
                    placeholder="Select tags"
                    data={tagSelectData}
                    {...form.getInputProps('tagIds')}
                    clearable
                    searchable
                  />
                </Stack>
              </Group>
              <Group grow>
                <Stack gap="xs">
                  <Text size="md" fw={600}>
                    Content{' '}
                    <Text span c="red">
                      *
                    </Text>
                  </Text>
                  <Button variant="outline" onClick={handleGoToBuilder} fullWidth>
                    {hasValidFormTemplateContent(surveyJSON)
                      ? 'Edit Form Fields in Builder'
                      : 'Design Form Fields in Builder'}
                  </Button>
                  {contentError && (
                    <Text size="sm" c="red">
                      {contentError}
                    </Text>
                  )}
                  {hasValidFormTemplateContent(surveyJSON) && (
                    <Text size="sm" c="green">
                      Form has {Array.isArray(surveyJSON.pages) ? surveyJSON.pages.length : 0} page(s) configured
                    </Text>
                  )}
                </Stack>
              </Group>
            </Stack>
          </Stack>

          <SaveErrorBox error={saveError} />

          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" loading={isSaving}>
              {isEditMode ? 'Save Changes' : 'Save as Draft'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};
